import { Env } from '@/libs/Env'

/**
 * API Client Error for better error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: Response,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Base API Client configuration
 */
interface BaseClientConfig {
  baseUrl?: string
  defaultHeaders?: Record<string, string>
}

/**
 * Base API Client class
 * All other API clients extend from this
 */
export class BaseApiClient {
  protected baseUrl: string
  protected defaultHeaders: Record<string, string>

  constructor(config: BaseClientConfig = {}) {
    this.baseUrl = config.baseUrl || (Env.VITE_API_URL as string)
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config.defaultHeaders,
    }
  }

  /**
   * Set authorization token
   */
  setAuthToken(token: string | null) {
    if (token) {
      this.defaultHeaders.Authorization = `Bearer ${token}`
    } else {
      delete this.defaultHeaders.Authorization
    }
  }

  /**
   * Generic fetch wrapper with error handling
   */
  protected async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`

    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...options.headers,
      },
    })

    if (!response.ok) {
      const errorMessage = `API request failed: ${response.status} ${response.statusText}`
      throw new ApiError(errorMessage, response.status, response)
    }

    return response.json()
  }

  /**
   * Helper method to build query strings
   */
  protected buildQueryString(params: Record<string, any>): string {
    const queryParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString())
      }
    })

    const queryString = queryParams.toString()
    return queryString ? `?${queryString}` : ''
  }
}
